<template>
  <div class="fwsed-overview-section">
    <content-loading v-if="isOverviewLoading" />
    <div v-else>
      <empty v-if="!hasData">
        <h5 class="text-center">{{ $t("firm.audit.dashboard.no-data") }}</h5>
      </empty>
      <div v-else>
        <div class="row">
          <div class="col-lg-6 mb-3">
            <div class="chart-card">
              <h5 class="card-title">{{ $t("firm.audit.dashboard.status-distribution") }}</h5>
              <apexchart v-if="statusChartHasData" type="pie" height="280" :options="statusChartOptions" :series="statusSeries" />
              <div v-else class="text-muted text-center py-4">
                {{ $t("firm.audit.dashboard.no-data") }}
              </div>
            </div>
          </div>
          <div class="col-lg-6 mb-3">
            <div class="metric-grid metric-grid--vertical">
              <div
                class="metric-card"
                v-for="card in metricCards"
                :key="card.label"
                :class="{ 'metric-card--clickable': card.clickable }"
                @click="card.clickable ? openFirmModal(card) : null"
              >
                <div class="metric-label">{{ card.label }}</div>
                <div class="metric-value">{{ card.value }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="firm-list-modal" :title="modalTitle" size="lg" hide-footer centered>
      <div v-if="isModalLoading" class="text-center py-5">
        <b-spinner variant="primary"></b-spinner>
      </div>
      <div v-else>
        <empty v-if="!modalFirms || modalFirms.length === 0">
          <h6 class="text-center">{{ $t("common.no-data") }}</h6>
        </empty>
        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t("firm.name") }}</th>
                  <th>{{ $t("firm.vkn") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr class="hand" v-for="item in modalFirms" :key="item._id" @click="goToFirmDetail(item)">
                  <td class="font-weight-bold">{{ item.firm.name | translate }}</td>
                  <td>{{ item.firm.vkn | translate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-center mt-4 pb-2" v-if="modalPagination">
            <b-pagination
              v-model="modalRequest.pagination.options.page"
              :total-rows="modalPagination.totalDocs"
              :per-page="modalPagination.limit"
              limit="5"
              size="sm"
              @input="fetchModalFirms"
            ></b-pagination>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";
import ApexChart from "vue-apexcharts";

export default {
  name: "FwsedOverviewSection",
  components: { ContentLoading, Empty, apexchart: ApexChart },
  data() {
    return {
      modalTitle: "",
      modalRequest: {
        pagination: {
          options: { page: 1, limit: 10, sort: { createdAt: "desc" } },
          query: {},
        },
      },
      overviewRequest: {
        pagination: {
          options: { page: 1, limit: 200, sort: { createdAt: "desc" } },
        },
      },
    };
  },
  computed: {
    overview() {
      return this.$store.getters["firmAudit/overview"];
    },
    isOverviewLoading() {
      return this.$store.getters["firmAudit/isOverviewLoading"];
    },
    statusCounts() {
      return this.overview?.statusCounts ?? {};
    },
    statusDistribution() {
      return Object.entries(this.statusCounts).map(([status, count]) => ({
        status,
        count,
        label: this.formatStatus(status),
      }));
    },
    statusSeries() {
      return this.statusDistribution.map((item) => item.count);
    },
    statusChartOptions() {
      return {
        chart: { type: "pie" },
        labels: this.statusDistribution.map((item) => item.label),
        legend: { position: "bottom" },
        colors: ["#4b72fa", "#17a2b8", "#28a745", "#ffc107", "#dc3545", "#6c757d"],
        dataLabels: { enabled: true },
      };
    },
    statusChartHasData() {
      return this.statusSeries.length > 0 && this.statusSeries.reduce((acc, val) => acc + val, 0) > 0;
    },
    metricCards() {
      const averages = this.overview?.averages ?? {};
      const support = this.overview?.supportService ?? {};
      const virtual = this.overview?.virtualServer ?? {};
      const totals = this.overview?.totals ?? {};
      return [
        { label: this.$t("firm.audit.dashboard.audit-count") || "Denetim sayısı", value: totals.audits ?? 0 },
        { label: this.$t("firm.audit.dashboard.total-firm-count") || "Toplam firma sayısı", value: totals.firms ?? 0 },
        { label: this.$t("firm.audit.dashboard.avg-test-duration"), value: this.$options.filters.duration(averages.testApproval) },
        { label: this.$t("firm.audit.dashboard.avg-prod-duration"), value: this.$options.filters.duration(averages.prodApproval) },
        {
          label: this.$t("firm.audit.dashboard.support-yes"),
          value: support.yes ?? 0,
          clickable: (support.yes ?? 0) > 0,
          filterKey: "supportService",
          filterValue: true,
        },
        {
          label: this.$t("firm.audit.dashboard.support-no"),
          value: support.no ?? 0,
          clickable: (support.no ?? 0) > 0,
          filterKey: "supportService",
          filterValue: false,
        },
        {
          label: this.$t("firm.audit.dashboard.virtual-yes"),
          value: virtual.yes ?? 0,
          clickable: (virtual.yes ?? 0) > 0,
          filterKey: "virtualServer",
          filterValue: true,
        },
        {
          label: this.$t("firm.audit.dashboard.virtual-no"),
          value: virtual.no ?? 0,
          clickable: (virtual.no ?? 0) > 0,
          filterKey: "virtualServer",
          filterValue: false,
        },
      ];
    },
    modalPagination() {
      return this.$store.getters["organizationFirm/summaryPagination"];
    },
    modalFirms() {
      return this.modalPagination?.docs ?? [];
    },
    isModalLoading() {
      return this.$store.getters["organizationFirm/isSummaryLoading"];
    },
    hasData() {
      if (!this.overview) {
        return false;
      }
      const totalStatuses = this.statusSeries.reduce((acc, val) => acc + val, 0);
      const totals = this.overview?.totals ?? {};
      const totalFirms = totals.firms ?? 0;
      const totalAudits = totals.audits ?? 0;
      return totalStatuses > 0 || totalFirms > 0 || totalAudits > 0;
    },
  },
  mounted() {
    this.loadOverview();
  },
  methods: {
    goToFirmDetail(item) {
      if (!item?.firm?._id) {
        return;
      }

      this.$router.push(`firm-management/${item.firm._id}/audit`);
    },
    async refresh() {
      await this.loadOverview();
    },
    async loadOverview() {
      try {
        await this.$store.dispatch("firmAudit/fetchOverview", this.overviewRequest);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async openFirmModal(card) {
      this.modalTitle = card.label;
      this.modalRequest.pagination.options.page = 1;
      this.modalRequest.pagination.query = {
        [card.filterKey]: { value: card.filterValue, isObject: true },
      };
      this.$bvModal.show("firm-list-modal");
      await this.fetchModalFirms();
    },
    async fetchModalFirms() {
      try {
        await this.$store.dispatch("organizationFirm/fetchSummary", this.modalRequest.pagination);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    formatStatus(status) {
      if (!status) {
        return "-";
      }
      const questionSetStatusMap = {
        FIRM_REVIEW: this.$t("firm.audit.status.firm-review"),
        BT_REVIEW: this.$t("firm.audit.status.bt-review"),
        COMPLETED: this.$t("firm.audit.status.completed"),
        IN_PROGRESS: this.$t("firm.audit.status.in-progress"),
        REJECTED: this.$t("firm.audit.status.rejected"),
        NONE: this.$t("firm.audit.status.none"),
      };
      const firmStatusMap = {
        REVIEW: this.$t("firm.status.review"),
        PRE_TEST: this.$t("firm.status.pre-test"),
        TEST: this.$t("firm.status.test"),
        PRE_PROD: this.$t("firm.status.pre-prod"),
        PROD: this.$t("firm.status.prod"),
        CANCELED: this.$t("firm.status.canceled"),
      };
      return firmStatusMap[status] || questionSetStatusMap[status] || status;
    },
    formatDuration(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      if (typeof value === "string") {
        return value;
      }
      if (typeof value === "number") {
        const formatted = value.toLocaleString("tr-TR", { maximumFractionDigits: 1 });
        return `${formatted} saat`;
      }
      return "-";
    },
  },
};
</script>

<style scoped>
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.metric-grid--vertical {
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
}

.metric-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
}

.metric-label {
  font-size: 0.85rem;
  color: #6c757d;
}

.metric-value {
  font-size: 1.6rem;
  font-weight: 600;
  margin-top: 0.35rem;
}

.metric-card--clickable {
  cursor: pointer;
  transition: all 0.2s ease;
  /* border-left: 4px solid #4b72fa; */
}

.metric-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);
  background: #f8faff;
}

/* .metric-card--clickable .metric-value {
  color: #4b72fa;
} */

.chart-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
  height: 100%;
}

.chart-card .card-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
</style>
